import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { DatePicker } from '@fikasio/react-ui-components';
import {
  useAddObjectiveEntryMutation,
  useDeleteObjectiveEntryMutation,
  useGetObjectiveEntriesQuery,
  usePatchObjectiveEntryMutation,
} from 'services/objectives/api';
import { Objective, ObjectiveEntry } from 'services/objectives/types';
import { toDateTime } from 'utils/date';
import { goalKindOf } from '../ObjectivesViewer/utils';
import ObjectiveProgressChart from './ObjectiveProgressChart';
import { compareTargets, formatTrackNumber } from './targetComparison';
import styles from './ObjectiveProgress.module.css';
import 'components/UI/Form.css';

interface ObjectiveProgressProps {
  objective: Objective;
}

const now = () => new Date();

const ObjectiveProgress = ({
  objective,
}: ObjectiveProgressProps) => {
  const { t } = useTranslation();
  const { data: entries = [] } = useGetObjectiveEntriesQuery(objective.id);
  const [addEntry] = useAddObjectiveEntryMutation();
  const [patchEntry] = usePatchObjectiveEntryMutation();
  const [deleteEntry] = useDeleteObjectiveEntryMutation();
  const [value, setValue] = useState('');
  const [recordedAt, setRecordedAt] = useState<Date | null>(now);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [note, setNote] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const kind = goalKindOf(objective);
  const latest = entries[0];
  const comparisons = compareTargets({
    entries,
    goalKind: kind,
    targets: objective.targets,
  });
  const best = entries.length === 0
    ? null
    : kind === 'HIGHEST'
      ? Math.max(...entries.map(entry => entry.value))
      : kind === 'LOWEST'
        ? Math.min(...entries.map(entry => entry.value))
        : null;
  const distance = kind === 'AROUND' && latest && objective.goal != null
    ? Math.abs(latest.value - objective.goal)
    : null;

  const resetForm = () => {
    setValue('');
    setNote('');
    setRecordedAt(now());
    setDatePickerKey(key => key + 1);
    setEditingId(null);
    setError(null);
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = Number(value);
    const recordedAtIso = recordedAt
      ? DateTime.fromJSDate(recordedAt).set({ millisecond: 0 }).toISO()
      : null;
    if (value.trim() === '' || Number.isNaN(parsed) || !recordedAtIso) {
      setError(t('required'));
      return;
    }
    const body = {
      note: note.trim(),
      objectiveId: objective.id,
      recordedAt: recordedAtIso,
      value: parsed,
    };
    const result = editingId
      ? await patchEntry({ ...body, id: editingId })
      : await addEntry(body);
    if ('error' in result && result.error) {
      setError(t('invalidSubmission'));
      return;
    }
    resetForm();
  };

  const startEdit = (entry: ObjectiveEntry) => {
    setEditingId(entry.id);
    setValue(String(entry.value));
    setRecordedAt(DateTime.fromISO(entry.recordedAt).toJSDate());
    setDatePickerKey(key => key + 1);
    setNote(entry.note || '');
    setError(null);
  };

  return (
    <div>
      <p>{t(`goalKindHelp${kind}`)}</p>
      <p>
        <b>{t('currentValue')}</b>
        {': '}
        {latest ? latest.value : '-'}
        {best != null && (
          <>
            {' · '}
            <b>{t('bestValue')}</b>
            {': '}
            {best}
          </>
        )}
        {distance != null && (
          <>
            {' · '}
            {t('distanceFromTarget', { distance })}
          </>
        )}
      </p>
      {
        comparisons.length > 0 && (
          <ul className={styles.comparisons}>
            {
              comparisons.map(item => {
                const expected = formatTrackNumber(item.expected);
                const text = item.relation === 'distance'
                  ? t('distanceFromTargetTrack', {
                    distance: formatTrackNumber(item.distance ?? 0),
                    expected,
                    name: item.name,
                  })
                  : t(item.relation === 'ahead' ? 'aheadOfTarget' : 'behindTarget', {
                    expected,
                    name: item.name,
                  });
                return <li key={item.id}>{text}</li>;
              })
            }
          </ul>
        )
      }
      <ObjectiveProgressChart entries={entries} objective={objective} />
      <form onSubmit={onSubmit}>
        {error && <Alert variant="danger">{error}</Alert>}
        <div style={{ alignItems: 'flex-end', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <label className="form-label" htmlFor="objective-entry-value">{t('value')}</label>
            <input
              className="form-control"
              id="objective-entry-value"
              onChange={event => setValue(event.target.value)}
              step="any"
              style={{ width: 120 }}
              type="number"
              value={value}
            />
          </div>
          <div className={styles.recordedAt}>
            <label className="form-label">{t('recordedOn')}</label>
            <br />
            <DatePicker
              className="form-control"
              defaultValue={recordedAt ?? undefined}
              displayFormat="yyyy-MM-dd HH:mm"
              key={datePickerKey}
              onChange={date => {
                setRecordedAt(DateTime.fromJSDate(date).set({ millisecond: 0 }).toJSDate());
              }}
              showTimeSelect
              timeCaption={t('hour')}
              timeFormat="HH:mm"
              timeIntervals={15}
            />
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label className="form-label" htmlFor="objective-entry-note">{t('note')}</label>
            <input
              className="form-control"
              id="objective-entry-note"
              onChange={event => setNote(event.target.value)}
              value={note}
            />
          </div>
          <Button type="submit" variant="success">
            {editingId ? t('save') : t('logProgress')}
          </Button>
          {editingId && (
            <Button
              onClick={resetForm}
              type="button"
              variant="outline-secondary"
            >
              {t('cancel')}
            </Button>
          )}
        </div>
      </form>
      {
        entries.length === 0
          ? <p style={{ marginTop: 16 }}>{t('noProgressEntries')}</p>
          : (
            <table style={{ marginTop: 16, width: '100%' }}>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {toDateTime(entry.recordedAt)?.toFormat('yyyy-MM-dd HH:mm')}
                    </td>
                    <td>{entry.value}</td>
                    <td>{entry.note}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button
                        onClick={() => startEdit(entry)}
                        size="sm"
                        type="button"
                        variant="outline-secondary"
                      >
                        {t('edit')}
                      </Button>
                      {' '}
                      <Button
                        onClick={() => deleteEntry({ id: entry.id, objectiveId: objective.id })}
                        size="sm"
                        type="button"
                        variant="outline-danger"
                      >
                        {t('delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
      }
    </div>
  );
};

export default ObjectiveProgress;
